/**
 * `applyOutcomeSuggestionVote` — per-row vote-aggregate mapper for the
 * `useGetOutcomeSuggestions` live list on
 * `morfi.market.${pubkey}.outcome_suggestion_voted.v0`.
 *
 * The subject placeholder is the MARKET pubkey (a route arg, not the
 * per-row suggestion id), so codegen dispatches every vote event through
 * a whole-list walk — the same shape as `recomputeNetVotes`. This helper is
 * called once per row; it patches
 * ONLY the row whose `id` matches the payload `suggestionId` and returns
 * every other row by reference (so identity-stable structural sharing is
 * preserved and offscreen cards don't re-render).
 *
 * The event carries POST-mutation aggregates, so the three counts are a
 * direct copy — no REST round-trip:
 *
 *   upvotes   ← raw.upvotes
 *   downvotes ← raw.downvotes
 *   score     ← raw.score
 *
 * `viewerKind` (the viewer's own up/down/cleared state) is NOT touched
 * here — it is per-viewer and patched optimistically by the
 * `useVoteOnOutcomeSuggestion` mutation, exactly as `recomputeNetVotes`
 * leaves `myVote` to the proposal-vote mutation.
 *
 * Payload shape (mirrors morfi.v1.cmd.OutcomeSuggestionVoteUpdate):
 *   { ts, marketPubkey, suggestionId, voterPubkey, kind, upvotes,
 *     downvotes, score }
 *
 * Immutable: returns a NEW object only when this is the target row AND at
 * least one count actually changes; otherwise the exact `prev` reference.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

function readField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const v = readField(raw, key);
  if (typeof v === 'bigint') return Number(v);
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readString(raw: Record<string, unknown>, key: string): string {
  const v = readField(raw, key);
  return typeof v === 'string' ? v : '';
}

export function applyOutcomeSuggestionVote<
  Row extends {
    readonly id?: string;
    readonly upvotes?: number;
    readonly downvotes?: number;
    readonly score?: number;
  },
>(prev: Row, raw: Record<string, unknown>, _args?: Record<string, unknown>): Row {
  const suggestionId = readString(raw, 'suggestionId');
  if (suggestionId.length === 0 || prev.id !== suggestionId) return prev;

  const upvotes = readNumber(raw, 'upvotes');
  const downvotes = readNumber(raw, 'downvotes');
  const score = readNumber(raw, 'score');

  const nextUpvotes = upvotes ?? prev.upvotes;
  const nextDownvotes = downvotes ?? prev.downvotes;
  const nextScore = score ?? prev.score;

  if (
    nextUpvotes === prev.upvotes &&
    nextDownvotes === prev.downvotes &&
    nextScore === prev.score
  ) {
    return prev;
  }
  return { ...prev, upvotes: nextUpvotes, downvotes: nextDownvotes, score: nextScore };
}
